'use client';

/**
 * Shared hook for the mode9 System Obeya cards — calls the
 * `system_obeya.view` strategy and returns the ObeyaViewResponse,
 * including the Layer 1 chrome labels.
 *
 * Same composite-payload pattern as use-obeya-project-data: every card
 * in the preset calls this hook with the same view_name; React Query
 * dedupes so N cards = 1 BFF call. The response carries `labels` —
 * cards slice their keys via the shared `L` constant; no chrome
 * strings live in TSX. See i18n_concept.md line 87.
 *
 * @see klyde_edge/klyde_docs/system_obeya/handler.py — system_obeya_view strategy
 * @see klyde_edge/klyde_docs/system_obeya/models.py — ObeyaViewResponse shape
 * @see klyde_edge/ui_config/labels/system_obeya.py — Layer 1 chrome
 */

import { useQuery } from '@tanstack/react-query';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { api } from '@/lib/api/client';

export interface ProductivityFields {
  trad_hours?: number;
  ai_hours?: number;
  saved_hours?: number;
  gain_pct?: number;
}

export interface ModuleRow extends ProductivityFields {
  module: string;
  files_count?: number;
  sloc?: number;
  commits?: number;
  function_points?: number;
  story_count?: number;
  capability_count?: number;
  feature_count?: number;
  declarative_pct?: number;
  active_days?: number;
  [k: string]: unknown;
}

export interface SystemObeyaTotals {
  total_sloc?: number;
  total_files?: number;
  total_fp?: number;
  total_stories?: number;
  total_features?: number;
  total_capabilities?: number;
  total_commits?: number;
  total_trad_hours?: number;
  total_ai_hours?: number;
  total_saved_hours?: number;
  total_gain_pct?: number;
  trad_cost_usd?: number;
  ai_cost_usd?: number;
  savings_usd?: number;
  calendar_days?: number;
  [k: string]: unknown;
}

export interface SystemObeyaConfig {
  traditional_team_size?: number;
  ai_team_size?: number;
  hours_per_day?: number;
  [k: string]: unknown;
}

export type SystemObeyaLabels = Record<string, string>;

export interface SystemObeyaViewResponse {
  view: string;
  timestamp: string;
  run_id?: string | null;
  data: ModuleRow[];
  count: number;
  totals?: SystemObeyaTotals | null;
  config?: SystemObeyaConfig | null;
  labels?: SystemObeyaLabels | null;
}

export function useSystemObeyaView(viewName: string, limit = 50) {
  const strategy = useCapabilityStore((s) => s.strategyName('system_obeya', 'view'));
  const lang = useLanguageStore((s) => s.lang);

  // `queryKey` is surfaced on the return so cards can pass it to
  // `useCardExport({query_key})` for capture-envelope derivation —
  // zero new knowledge, just the hook's existing identity made
  // addressable to its callers. The hook is the single source of
  // truth for this key; cards don't reconstruct it.
  const queryKey = ['system-obeya-view', strategy, viewName, limit, lang] as const;
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.getStrategyData<SystemObeyaViewResponse>(
        strategy ?? 'system_obeya_view',
        { view_name: viewName, limit, ...(lang ? { lang } : {}) },
      );
      if (!res.success) throw new Error(res.error || `${strategy} failed`);
      return res.data;
    },
    enabled: !!viewName,
    staleTime: 30 * 1000,
  });
  return { ...query, queryKey };
}
